import { memo, useCallback, useMemo } from 'react';

import {
  alpha,
  Box,
  Chip,
  makeStyles,
  Slide,
  useTheme,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';

import { FunnelTag } from 'store/reducers/funnels';

const TAG_MAX_LENGTH = 14;
const TAG_ICON_SIZE = 14;

const useStyles = makeStyles(({ spacing, typography }) => ({
  chipLabel: {
    fontSize: typography.pxToRem(10),
    fontWeight: typography.fontWeightMedium as number,
    cursor: 'pointer',
    paddingLeft: spacing(1.5),
  },
}));

interface TagItemProps {
  tag: FunnelTag;
  tagAdd?: string;
  onRemove?: (tagId?: string) => void;
}

const TagItem = ({ tag, tagAdd, onRemove }: TagItemProps) => {
  const classes = useStyles();
  const theme = useTheme();

  const { id, name, color } = tag;

  const colors = useMemo(() => {
    const isDark = theme.palette.type === 'dark';
    const baseColor =
      color || (isDark ? theme.palette.grey[300] : theme.palette.text.primary);

    return {
      backgroundColor: color,
      borderColor: alpha(baseColor, 0.3),
      textColor: theme.palette.getContrastText(baseColor),
    };
  }, [color, theme]);

  const tagLabel =
    name && name.length > TAG_MAX_LENGTH
      ? `${name.slice(0, TAG_MAX_LENGTH)}...`
      : name;

  const handleDelete = useCallback(() => {
    if (onRemove) onRemove(id);
  }, [onRemove, id]);

  const chipElement = (
    <Box title={name || ''}>
      <Chip
        deleteIcon={
          <Close
            titleAccess="Remover tag"
            style={{
              width: TAG_ICON_SIZE,
              height: TAG_ICON_SIZE,
              color: colors.textColor,
            }}
          />
        }
        size="small"
        classes={{
          labelSmall: classes.chipLabel,
        }}
        label={tagLabel}
        style={{
          backgroundColor: colors.backgroundColor,
          border: `1px solid ${colors.borderColor}`,
          color: colors.textColor,
        }}
        onDelete={onRemove ? handleDelete : undefined}
        role="button"
        aria-label={
          onRemove ? `Tag ${name}, clique para remover` : `Tag ${name}`
        }
      />
    </Box>
  );

  return id === tagAdd ? (
    <Slide in direction="left">
      {chipElement}
    </Slide>
  ) : (
    chipElement
  );
};

const MemoizedTagItem = memo(TagItem, (prevProps, nextProps) => {
  return (
    prevProps.tag.id === nextProps.tag.id &&
    prevProps.tag.color === nextProps.tag.color &&
    prevProps.onRemove === nextProps.onRemove
  );
});

export { MemoizedTagItem as TagItem };
